/**
 * SheetPlanPickerModal — pick an already-completed Architectural Drawing Sheet as a Program
 * Model level's plan background, instead of downloading + re-uploading a PDF that's already
 * attached to a Sheet Revision. See docs/PROGRAM_MODEL.md.
 *
 * Lists the project's Architectural sheets that have a PDF on their latest revision (a sheet
 * with no revision/PDF yet isn't "completed" — excluded, not shown as a dead entry). On pick,
 * re-resolves the PDF URL fresh (preferring the stable, QC-approved Supabase URL via
 * getOfficialSheetPdf, falling back to the sheet's latest Airtable revision URL) — Airtable
 * attachment URLs are time-limited signed links that can expire between the list load and the
 * click, so the URL captured at list time is never trusted for the actual fetch.
 *
 * Hands the caller a real Blob (not a File — this app fetches the sheet's own hosted PDF, it
 * never touches the user's filesystem) wrapped as { blob, name } so ProgramModelModal can feed
 * it through the exact same handlePlanFile pipeline a manual upload uses.
 */
import { useState, useEffect, useMemo } from 'react'
import { getProjectSheetsWithLatestPdf, getOfficialSheetPdf } from '../../services/sheets.js'
import { getDiscipline } from '../DrawingSheetList.jsx'
import { alertDialog } from '../Dialog.jsx'

export default function SheetPlanPickerModal({ projectId, onSelect, onClose }) {
  const [sheets, setSheets] = useState(null) // null = loading, [] = loaded (possibly empty)
  const [query, setQuery] = useState('')
  const [resolvingId, setResolvingId] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    getProjectSheetsWithLatestPdf(projectId)
      .then(list => {
        if (cancelled) return
        setSheets(list.filter(s => s.pdfUrl && getDiscipline(s.fields['Sheet Number and Title'] || s.fields['Sheet Number'] || '') === 'Architectural'))
      })
      .catch(e => { if (!cancelled) { setSheets([]); setError(e.message || 'Could not load drawing sheets.') } })
    return () => { cancelled = true }
  }, [projectId])

  const filtered = useMemo(() => {
    if (!sheets) return []
    const q = query.trim().toLowerCase()
    if (!q) return sheets
    return sheets.filter(s => (s.fields['Sheet Number and Title'] || '').toLowerCase().includes(q))
  }, [sheets, query])

  async function handlePick(sheet) {
    if (resolvingId) return
    setResolvingId(sheet.id)
    try {
      const { url } = await getOfficialSheetPdf(sheet.id, sheet.pdfUrl, sheet.latestRevisionId)
      if (!url) throw new Error('This sheet no longer has a usable PDF.')
      const resp = await fetch(url)
      if (!resp.ok) throw new Error('Could not download the sheet PDF (HTTP ' + resp.status + ').')
      const blob = await resp.blob()
      onSelect({ blob, name: (sheet.fields['Sheet Number and Title'] || 'Sheet') + '.pdf' })
    } catch (e) {
      setResolvingId(null)
      await alertDialog({ title: 'Could not use this sheet', message: e.message || 'The sheet PDF could not be loaded.' })
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
      onPointerDown={e => e.stopPropagation()}
    >
      <div
        style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '22px 24px', width: 460, maxWidth: '92vw', maxHeight: '78vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
          <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--aj-text)', flex: 1 }}>
            Select a Drawing Sheet
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--aj-text-muted)', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 2 }} title="Close (Esc)">×</button>
        </div>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', lineHeight: 1.45, marginBottom: 12 }}>
          Architectural sheets with a completed revision PDF. Picking one uses that PDF directly as this level's plan — nothing to download or re-upload.
        </div>

        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search sheet number or title…" autoFocus
          style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 10px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none', marginBottom: 10 }}
        />

        <div style={{ flex: 1, overflowY: 'auto', border: '0.5px solid var(--aj-border)', borderRadius: 6 }}>
          {sheets === null && (
            <div style={{ padding: '16px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>Loading sheets…</div>
          )}
          {sheets !== null && !sheets.length && (
            <div style={{ padding: '16px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', lineHeight: 1.5 }}>
              {error || 'No completed Architectural sheets with a PDF yet — upload a plan PDF instead.'}
            </div>
          )}
          {sheets !== null && sheets.length > 0 && !filtered.length && (
            <div style={{ padding: '16px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>No sheets match "{query}".</div>
          )}
          {filtered.map(s => {
            const busy = resolvingId === s.id
            return (
              <button
                key={s.id}
                onClick={() => handlePick(s)}
                disabled={!!resolvingId}
                className="aj-pm-sheetrow"
                style={{
                  display: 'flex', width: '100%', alignItems: 'center', gap: 10, textAlign: 'left',
                  padding: '8px 12px', background: 'transparent', border: 'none', borderBottom: '0.5px solid var(--aj-border)',
                  cursor: resolvingId ? 'default' : 'pointer', opacity: resolvingId && !busy ? 0.5 : 1,
                }}
              >
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)', flexShrink: 0 }}>
                  {s.fields['Sheet Number'] || ''}
                </span>
                <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.fields['Sheet Title'] || s.fields['Sheet Number and Title'] || 'Untitled sheet'}
                </span>
                {busy && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)', flexShrink: 0 }}>Loading…</span>}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
